import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import BodyText from 'LuxKit/Typography/BodyText'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'LuxKit/Typography/Heading'
import { trackOfferPageSuggestedPackageViewPackageClick } from 'components/OfferPage/analytics'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { isRewardFareClass, toBaseFareClass } from 'constants/flight'
import { computeRewardBundlePrice, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import { FormattedMessage, useIntl } from 'react-intl'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { mediaQueryDown } from 'components/utils/breakpoint'

const Root = styled.div`
  border: 1px solid ${props => props.theme.palette.highlight.primary.darkForeground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const ContentContainer = styled.div`
  flex: 1;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  padding: ${rem(20)};
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: stretch;
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const DesktopImageContainer = styled.div`
  width: ${rem(182)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileRoomImageWrap = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    width: 100%;
    align-self: stretch;
  }
`

interface Props {
  suggestedOption: App.OfferPackageOptionView
  message: string
  /** Fallback behaviour when clicking "View package", for Safari and other crap browsers that don't support `checkVisibility` */
  viewPackageFallback: () => void
}

function SuggestedOptionPreview({ suggestedOption, message, viewPackageFallback }: Props) {
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { offer, checkInDate, duration, charterBundleRoute, priceMode, rooms, fareClass } = offerPageState
  const isSameRoomMode = useAppSelector(appState => !isMultipleRoomBookingActive(offerPageState, appState))

  const { totalFlightPrice: flightPrice, rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal } = useOfferPageFlightPrice()

  const pricing = suggestedOption.pricing
  const memberPricing = suggestedOption.memberPricing
  const pricingWithFlights = flightPrice ? addFlightPrice(pricing, flightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && flightPrice) ? addFlightPrice(memberPricing, flightPrice) : undefined

  // Charter bundle fallback pricing (matches FlashOfferSummary). Needed when no specific check-in
  // is selected, or when the check-in doesn't match a charter date — otherwise the flight
  // portion above falls back to `metadata.cheapest.totalPrice`, which is the search-response
  // aggregate and ignores Friends & Family pricing. The fallback walks per-date calendar
  // entries (each FNF-aware via `useFlightItemData`), so the summary, offer tile and this
  // suggested-option preview all surface the same FNF-correct floor price.
  const suggestedOptionPkg = suggestedOption.package
  // Both hooks take the base cabin (cash) — reward variants share dates/journey data with
  // their base cabin so the date / fallback bundle is identical.
  const baseFareClassForFallback = toBaseFareClass(fareClass)
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate,
    nights: suggestedOptionPkg.duration,
    charterBundleRoute,
    rooms,
    fareClass: baseFareClassForFallback,
  })
  const isCheckInMatchingCharterDate = !!checkInDate && !!charterDepartDate
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkInDate || !isCheckInMatchingCharterDate)
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass: baseFareClassForFallback,
    nights: suggestedOptionPkg.duration,
    disabled: !shouldUseFallbackPricing,
  })

  const forceBundle = !!(charterBundleRoute || offer?.bundledWithFlightsOnly)
  const pricingToShow = !forceBundle
    ? pricing
    : shouldUseFallbackPricing && fallbackPricing
      ? fallbackPricing
      : pricingWithFlights
  const memberPricingToShow = !forceBundle
    ? memberPricing
    : shouldUseFallbackPricing && fallbackPricing
      ? fallbackMemberPricing
      : memberPricingWithFlights

  const pkg = suggestedOption.package
  const roomType = pkg.roomType
  const firstImage = roomType?.images?.[0]

  const { inclusions, bonusInclusions } = useOfferInclusions(pkg)
  const allInclusions = useMemo(() => [...bonusInclusions, ...inclusions], [bonusInclusions, inclusions])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: suggestedOption,
      currentRegionCode,
      nights: duration,
    }),
  ], [offer, suggestedOption, currentRegionCode, duration])

  const scrollToPackage = useCallback(() => {
    trackOfferPageSuggestedPackageViewPackageClick()
    // Can't just use scrollToId here, because there are actually two elements with the same ID for
    // each package - one for mobile and one for desktop.
    const anchors = document.querySelectorAll(`#${CSS.escape(roomType.id)}`)
    const target = [...anchors].find(el => el.checkVisibility?.())
    if (target) {
      target.scrollIntoView({ behavior: 'auto' })
    } else {
      viewPackageFallback()
    }
  }, [roomType.id, viewPackageFallback])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null
  const rewardPriceData = useMemo(() => {
    if (!isRewardSelected || !pricingToShow) return undefined
    const hotelPrice = pricingToShow.price - (flightPrice ?? 0)
    const hotelMemberPrice = memberPricingToShow ? memberPricingToShow.price - (flightPrice ?? 0) : undefined
    return computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice,
      rewardPointsTotal: rewardSeatPointsTotal,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
    })
  }, [isRewardSelected, pricingToShow, memberPricingToShow, flightPrice, rewardSeatPointsTotal, rewardSeatCashCoPayTotal])

  const isRewardFareSelected = isRewardFareClass(fareClass)
  const needsRewardPreview = isRewardFareSelected && !isRewardSelected

  const totalGuests = useMemo(() => Math.max(1, sumUpOccupancies(rooms)), [rooms])

  const rewardPreview = useMemo(() => {
    if (!needsRewardPreview) return undefined
    const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClass)]
    if (!rate) return undefined
    const hotelPrice = pricing.price
    const hotelMemberPrice = memberPricing?.price
    const pts = rate.pointsPerPerson * totalGuests
    const cash = hotelPrice + rate.cashPerPerson * totalGuests
    const memberCash = hotelMemberPrice ? hotelMemberPrice + rate.cashPerPerson * totalGuests : undefined
    return { points: pts, cash, memberCash }
  }, [needsRewardPreview, fareClass, pricing.price, memberPricing?.price, totalGuests])

  const nightsText = useMemo(() => {
    const nights = intl.formatMessage(
      {
        id: '1ZgK0L_Ngfs-n0KvQPTe',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Number of nights for price caption in the pricing or cost breakdown section',
      },
      { count: duration },
    )

    return checkInDate
      ? nights
      : intl.formatMessage(
          {
            id: 'dAvWpPRm0DQRe8XuTkn9',
            defaultMessage: '{nights} from',
            description: 'Price caption with number of nights and from text for prices without specific dates',
          },
          { nights },
        )
  }, [intl, duration, checkInDate])

  if (!offer || !roomType) return null

  const roomImageId = firstImage?.id ?? NO_IMAGE_AVAILABLE_ID

  const rewardUnitLabel = priceMode === 'perPerson'
    ? intl.formatMessage({
        id: '-lwhQ0fhXpw1HZWPqtFw',
        defaultMessage: 'person',
        description: 'Per-person pricing unit suffix for reward price on the suggested option preview tile',
      })
    : intl.formatMessage({
        id: 'j--PuUYLTgWUxAxROh0Z',
        defaultMessage: 'total',
        description: 'Total pricing unit suffix in the Maldives calendar',
      })

  return <Root>
    <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{message}</MessageBanner>
    <Group direction="horizontal" verticalAlign="stretch">
      <DesktopImageContainer>
        <HeightFillingImage
          id={roomImageId}
          width={182}
          mobileWidth="182px"
          fit="center"
          quality="good"
          dpr={2}
        />
      </DesktopImageContainer>
      <ContentContainer>
        <Content>
          <DetailsColumn>
            <Group direction="vertical" gap={8}>
              <CSSBreakpoint max="mobile">
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <MobileRoomImageWrap>
                    <ResponsiveImage
                      id={roomImageId}
                      width={64}
                      height={64}
                      mobileWidth="64px"
                      aspectRatio="1"
                      fit="center"
                      quality="good"
                      dpr={2}
                    />
                  </MobileRoomImageWrap>
                  <Heading variant="heading4">{roomType.name}</Heading>
                </Group>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <BodyText variant="large" weight="bold">{roomType.name}</BodyText>
              </CSSBreakpoint>

              {offer.property && pkg.roomRate?.cancellationPolicy && (
                <FlashCancellationPolicies
                  checkInDate={checkInDate}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                />
              )}

              <LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={checkInDate ? 'exact' : 'estimate'}
                offer={offer}
              />

              {allInclusions.length > 0 && (
                <OfferInclusionsList
                  inclusions={allInclusions}
                  truncateAfter={2}
                  size="small"
                  onShowMoreClick={isSameRoomMode ? scrollToPackage : undefined}
                  showMoreText={intl.formatMessage({
                    id: 'pnzs4q516eNSNId9yp3-',
                    defaultMessage: 'View more inclusions',
                    description: 'Link text to view more inclusions in suggested option preview',
                  })}
                />
              )}
            </Group>
          </DetailsColumn>

          <PricingColumn direction="vertical" gap={16} verticalAlign="end">
            {rewardPriceData && <Group direction="vertical" horizontalAlign="start">
              <PriceRowPriceCaption>{nightsText}</PriceRowPriceCaption>
              <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
                <Heading variant="heading5" colour={rewardPriceData.memberCash !== undefined ? 'lux-plus' : undefined}>
                  <FormattedMessage
                    id="thjWZbXy5DndVyda47aB"
                    defaultMessage="{points} + {cash}"
                    description="Reward-seat bundle price on the suggested option preview tile on the offer page."
                    values={{
                      points: <FormatLoyaltyPoints points={convertToDisplay(rewardPriceData.points)} abbreviated />,
                      cash: <FormatCurrency value={convertToDisplay(rewardPriceData.memberCash ?? rewardPriceData.cash)} format="roundedDollar" />,
                    }}
                  />
                </Heading>
                <Caption variant="medium" colour={rewardPriceData.memberCash !== undefined ? 'lux-plus' : 'neutral-three'} weight="normal">
                  {rewardUnitLabel}
                </Caption>
              </Group>
              {rewardPriceData.memberCash !== undefined && (
                <Caption variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="FfM6_AIbUtHLt4RMQJCG"
                    defaultMessage="Non-member {points} + {cash}"
                    description="Non-member reward price on the suggested option preview tile."
                    values={{
                      points: <FormatLoyaltyPoints points={convertToDisplay(rewardPriceData.points)} abbreviated />,
                      cash: <FormatCurrency value={convertToDisplay(rewardPriceData.cash)} format="roundedDollar" />,
                    }}
                  />
                </Caption>
              )}
            </Group>}
            {!rewardPriceData && needsRewardPreview && rewardPreview && <Group direction="vertical" horizontalAlign="start">
              <PriceRowPriceCaption>{nightsText}</PriceRowPriceCaption>
              <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
                <Heading variant="heading5" colour={rewardPreview.memberCash !== undefined ? 'lux-plus' : undefined}>
                  <FormattedMessage
                    id="9h5LgVVdVQR3VK4aBwku"
                    defaultMessage="{points} + {cash}"
                    description="Reward-seat preview bundle price on the suggested option preview tile showing estimated loyalty points and cash co-pay when actual reward data is unavailable."
                    values={{
                      points: <FormatLoyaltyPoints points={convertToDisplay(rewardPreview.points)} abbreviated />,
                      cash: <FormatCurrency value={convertToDisplay(rewardPreview.memberCash ?? rewardPreview.cash)} format="roundedDollar" />,
                    }}
                  />
                </Heading>
                <Caption variant="medium" colour={rewardPreview.memberCash !== undefined ? 'lux-plus' : 'neutral-three'} weight="normal">
                  {rewardUnitLabel}
                </Caption>
              </Group>
              {rewardPreview.memberCash !== undefined && (
                <Caption variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="FfM6_AIbUtHLt4RMQJCG"
                    defaultMessage="Non-member {points} + {cash}"
                    description="Non-member reward price on the suggested option preview tile."
                    values={{
                      points: <FormatLoyaltyPoints points={convertToDisplay(rewardPreview.points)} abbreviated />,
                      cash: <FormatCurrency value={convertToDisplay(rewardPreview.cash)} format="roundedDollar" />,
                    }}
                  />
                </Caption>
              )}
            </Group>}
            {!rewardPriceData && !(needsRewardPreview && rewardPreview) && !!pricingToShow && <Group direction="vertical" horizontalAlign="start">
              <PriceRowPriceCaption>
                {nightsText}
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="M"
                price={convertToDisplay(pricingToShow.price)}
                memberPrice={memberPricingToShow?.price && convertToDisplay(memberPricingToShow.price)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                offerConfig={offer.luxPlus}
                horizontalAlign="start"
              />
              <PriceRowTaxesAndFees />
            </Group>}

            {isSameRoomMode && <TextButton
              kind="primary"
              size="small"
              fit="mobile-full-width"
              onClick={scrollToPackage}
            >
              {intl.formatMessage({
                id: '7z4-Ngi2aAhJS1Y3B-TV',
                defaultMessage: 'View package',
                description: 'Button text to view package details in suggested option preview',
              })}
            </TextButton>}
          </PricingColumn>
        </Content>
      </ContentContainer>
    </Group>
  </Root>
}

export default SuggestedOptionPreview
